import React, { useState } from "react";
import { format, addDays, addMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend, getYear } from "date-fns";
import { it } from "date-fns/locale";
import { CalendarDays, X, CalendarRange, CalendarPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const toIso = (d) => format(d, "yyyy-MM-dd");
const fromIso = (s) => new Date(`${s}T00:00:00`);
const SOGLIA_STRAORDINARIO_LABEL = 2;

// Verifica lato server i limiti giornalieri (8h ordinarie bloccanti, >2h straordinario avviso).
// righe: [{dipendente_id, data, ore, ore_straordinarie}] -> { ok, bloccate, avvisi }
export async function verificaLimitiOre(api, righe) {
  return (await api.post("/ore/verifica", { righe })).data;
}

function pasqua(anno) {
  const a = anno % 19, b = Math.floor(anno / 100), c = anno % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mese = Math.floor((h + l - 7 * m + 114) / 31), giorno = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(anno, mese - 1, giorno);
}

export function festivitaItaliane(anno) {
  const fisse = ["01-01", "01-06", "04-25", "05-01", "06-02", "08-15", "11-01", "12-08", "12-25", "12-26"];
  const out = fisse.map((md) => `${anno}-${md}`);
  out.push(toIso(addDays(pasqua(anno), 1)));
  return out;
}
export const isFestivo = (d) => festivitaItaliane(getYear(d)).includes(toIso(d));
const lavorativi = (da, a) => eachDayOfInterval({ start: da, end: a }).filter((d) => !isWeekend(d) && !isFestivo(d)).map(toIso);

// giorni: array 'YYYY-MM-DD'; onChange(nuovoArray)
export function SelettoreGiorni({ giorni, onChange, testid = "selettore-giorni" }) {
  const [open, setOpen] = useState(false);
  const [mese, setMese] = useState(new Date());
  const selected = giorni.map(fromIso);
  const ultimo = giorni.length ? fromIso(giorni[giorni.length - 1]) : null;
  const unisci = (nuovi) => onChange([...new Set([...giorni, ...nuovi])].sort());

  const aggiungiSettimana = () => {
    const base = ultimo ? addDays(startOfWeek(ultimo, { weekStartsOn: 1 }), 7) : new Date();
    const inizio = startOfWeek(base, { weekStartsOn: 1 });
    unisci(lavorativi(inizio, endOfWeek(base, { weekStartsOn: 1 })));
    setMese(inizio);
  };
  const aggiungiMese = () => {
    const giaMese = giorni.some((g) => g.startsWith(format(mese, "yyyy-MM")));
    const target = giaMese && ultimo ? addMonths(startOfMonth(ultimo), 1) : mese;
    unisci(lavorativi(startOfMonth(target), endOfMonth(target)));
    setMese(target);
  };

  const anni = [getYear(mese) - 1, getYear(mese), getYear(mese) + 1];
  const festivi = anni.flatMap(festivitaItaliane).map(fromIso);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="bg-white" data-testid={`${testid}-trigger`}>
            <CalendarDays className="h-4 w-4 mr-1.5" />
            {giorni.length === 0 ? "Più giorni…" : `${giorni.length} giorn${giorni.length === 1 ? "o" : "i"} selezionat${giorni.length === 1 ? "o" : "i"}`}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" data-testid={`${testid}-popover`}>
          <div className="flex flex-wrap gap-2 px-3 pt-3">
            <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={aggiungiSettimana} data-testid={`${testid}-settimana`}>
              <CalendarPlus className="h-3.5 w-3.5 mr-1" /> Seleziona settimana
            </Button>
            <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={aggiungiMese} data-testid={`${testid}-mese`}>
              <CalendarRange className="h-3.5 w-3.5 mr-1" /> Seleziona mese intero
            </Button>
          </div>
          <p className="px-3 pt-1.5 text-[11px] text-slate-400">Solo giorni lavorativi (Lun–Ven, senza festività). Ripremi per aggiungere la settimana/mese successivo. I festivi restano selezionabili a mano.</p>
          <Calendar
            mode="multiple"
            locale={it}
            weekStartsOn={1}
            month={mese}
            onMonthChange={setMese}
            selected={selected}
            onSelect={(days) => onChange((days || []).map(toIso).sort())}
            modifiers={{ festivo: festivi, weekend: (d) => isWeekend(d) }}
            modifiersClassNames={{
              festivo: "text-red-600 font-semibold aria-selected:text-white ring-1 ring-red-200 aria-selected:ring-0",
              weekend: "text-slate-400 aria-selected:text-white",
            }}
          />
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-red-300 bg-red-50" /> festivo</span>
            <div className="flex gap-2">
              {giorni.length > 0 && (
                <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onChange([])} data-testid={`${testid}-svuota`}>
                  Svuota
                </Button>
              )}
              <Button type="button" size="sm" className="h-7 text-xs" onClick={() => setOpen(false)} data-testid={`${testid}-conferma`}>
                Conferma
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {giorni.length > 0 && (
        <div className="flex flex-wrap gap-1 max-w-xl" data-testid={`${testid}-chips`}>
          {giorni.map((g) => {
            const d = fromIso(g);
            const speciale = isFestivo(d) || isWeekend(d);
            return (
              <span key={g} className={`inline-flex items-center gap-1 rounded-full border text-xs px-2 py-0.5 ${speciale ? "bg-red-50 border-red-200 text-red-700" : "bg-orange-50 border-orange-200 text-primary"}`} title={speciale ? "Giorno festivo o weekend" : ""}>
                {format(d, "dd/MM", { locale: it })}
                <button type="button" onClick={() => onChange(giorni.filter((x) => x !== g))} className="hover:text-red-600" aria-label="Rimuovi giorno">
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Conferma prima del salvataggio multi-giorno
export function ConfermaMultiGiorno({ open, onOpenChange, onConfirm, giorni, righePerGiorno = 1, dipendente, loading, avvisi = [] }) {
  const n = giorni.length;
  const speciali = giorni.filter((g) => { const d = fromIso(g); return isFestivo(d) || isWeekend(d); });
  const multi = n > 1 || righePerGiorno > 1;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="conferma-multi-giorno">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display">{avvisi.length > 0 && !multi ? "Attenzione: straordinario elevato" : "Conferma registrazione"}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm">
              {multi && (
              <p>
                Verranno create <b>{n * righePerGiorno} righe</b> su <b>{n} giorni</b>
                {dipendente ? <> per <b>{dipendente}</b></> : null}
                {n > 0 && <> ({format(fromIso(giorni[0]), "dd/MM/yyyy")} → {format(fromIso(giorni[n - 1]), "dd/MM/yyyy")})</>}.
              </p>
              )}
              {avvisi.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 space-y-1" data-testid="conferma-avvisi-straordinario">
                  <p className="font-semibold">Straordinario oltre {SOGLIA_STRAORDINARIO_LABEL} ore nella giornata (su tutte le commesse):</p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {avvisi.map((a, i) => <li key={i}>{a.messaggio}</li>)}
                  </ul>
                  <p className="text-xs">Puoi comunque procedere.</p>
                </div>
              )}
              {speciali.length > 0 && (
                <p className="text-red-700" data-testid="conferma-multi-giorno-festivi">
                  Attenzione: {speciali.length} giorn{speciali.length === 1 ? "o" : "i"} selezionat{speciali.length === 1 ? "o" : "i"} cade nel weekend o in una festività ({speciali.map((g) => format(fromIso(g), "dd/MM")).join(", ")}).
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="conferma-multi-giorno-annulla">Annulla</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); onConfirm(); }} disabled={loading} data-testid="conferma-multi-giorno-ok">
            {loading ? "Registrazione…" : "Registra"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
